import { Component, Input, OnChanges, OnInit, ViewChild, inject } from '@angular/core';
import { ExtensionComponentType, ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import {
  CheckoutComponentMetric,
  ExtensionsService,
} from '@app/pages/checkout-app/services/extensions/extensions.service';
import { ShopService } from '@app/services/shops/shop.service';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { Store } from '@ngrx/store';
import { catchError, filter, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { EMPTY, Subject } from 'rxjs';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { abbreviateLargeNumber } from '@utils/string-utils';
import { ExtComponentTypesOptions } from '../extension-header/config';
import { DateRangeSelection } from '@app/storybook/components/datepicker/date-range-presets';

@Component({
  selector: 'checkout-metrics',
  templateUrl: './checkout-metrics.component.html',
  styleUrls: ['./checkout-metrics.component.scss'],
  standalone: false,
})
export class CheckoutMetricsComponent implements OnInit, OnChanges {
  @Input() type: ExtensionComponentType;
  @Input() disabled;

  @ViewChild('datePicker') datePicker;

  label;
  displayHeader: boolean;
  metricsOptions = [];
  extensionTypeOption;
  metrics;
  loadingMetrics = false;
  shopCurrencySymbol = this.shopService.shopCurrencySymbol;
  readonly shopCreated = inject(ShopService).shopCreatedSignal;
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  static supportedExtensions = ExtComponentTypesOptions.filter((extension) => extension.metrics?.length).map(
    (extension) => extension.value,
  );
  @onDestroyComplete() destroy$ = new Subject();
  private metricsRange$ = new Subject<DateRangeSelection>();

  constructor(
    private extensionsService: ExtensionsService,
    private shopService: ShopService,
    private store: Store<CheckoutReducer.State>,
  ) {}

  ngOnInit() {
    this.subscribeToMetrics();
    this.loadedExtension$
      .pipe(takeUntil(this.destroy$))
      .subscribe((extension: ICheckoutExtensions & { metrics: CheckoutComponentMetric[] }) => {
        // a save of the same extension keeps the range the merchant picked
        const isSameExtension = !!extension?.id && extension.id === this.loadedExtension?.id;
        this.loadedExtension = extension;
        if (extension && !isSameExtension) {
          const options = ExtComponentTypesOptions.find((ex) => ex.value === extension.type);
          this.metrics = [];
          if (options) {
            this.extensionTypeOption = options;
            this.label = options.label;
            this.displayHeader = !!options.metrics;
            if (options.metrics?.length) {
              this.metricsOptions = options.metrics;
              // empty cards that show the loader, preloaded metrics cover 30 days and the picker sends all time
              this.processMetrics([]);
            }
          }
        }
      });
  }

  ngOnChanges(changes) {
    if (changes.type?.currentValue !== changes.type?.previousValue) {
      const options = ExtComponentTypesOptions.find((ex) => ex.value === this.type);
      this.metrics = [];
      if (options) {
        this.label = options.label;
        this.displayHeader = !!options.metrics;
      } else {
        this.displayHeader = false;
        this.metricsOptions = [];
      }
    }
  }

  loadMetrics(range: DateRangeSelection) {
    this.metricsRange$.next(range);
  }

  private subscribeToMetrics() {
    this.metricsRange$
      .pipe(
        filter(() => !!this.loadedExtension?.id && !!this.metricsOptions.length),
        switchMap((range) => {
          this.loadingMetrics = true;
          return this.extensionsService
            .loadMetrics(
              this.shopService.shopId,
              new Date(range.start).toISOString().slice(0, 10),
              new Date(range.end).toISOString().slice(0, 10),
              this.loadedExtension.id,
              this.loadedExtension.type as ExtensionComponentType,
            )
            .pipe(
              // a failed range must not end the stream, the next pick should still load
              catchError(() => EMPTY),
              finalize(() => (this.loadingMetrics = false)),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((metrics: CheckoutComponentMetric[]) => this.processMetrics(metrics));
  }

  private processMetrics(metrics: CheckoutComponentMetric[]) {
    const metricsToProcess = this.extensionTypeOption.dataInterceptor
      ? this.extensionTypeOption.dataInterceptor(metrics)
      : metrics;

    this.metrics = this.metricsOptions.map((metricOption) => {
      const metricData = metricsToProcess.find((m) => m.METRIC_NAME === metricOption.field);
      const title = metricOption.title;
      const prefix = metricOption.displayCurrency ? this.shopCurrencySymbol : '';
      const rawValue = Number(metricData?.METRIC_TOTAL_VALUE ?? 0);
      const { suffix, value } = abbreviateLargeNumber(metricOption.displayCurrency ? Math.round(rawValue) : rawValue);
      return {
        title,
        suffix,
        prefix,
        value,
        tooltip: metricOption.tooltip,
      };
    });
  }
}
